import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiAlert from "components/SuiAlert";
import { CircularProgress } from "@mui/material";
import ProcesosApi from "api/procesosApi";
import { obtenerParametrosGenerales } from "hooks/useParametrosGenerales";
import { styles } from "../styles";

// Función de validación externa
export const validateDeudas = async (procesoId) => {
  try {
    const response = await ProcesosApi.obtenerDeudasProceso(procesoId);
    const deudas = response.data.deudas || [];
    // Supuestos de insolvencia configurables (PORC_MIN_MORA y MIN_ACREEDORES_MORA)
    const parametros = await obtenerParametrosGenerales();
    const porcentajeMinimo = parametros.PORC_MIN_MORA;
    const minimoAcreedores = parametros.MIN_ACREEDORES_MORA;
    if (!deudas.length) {
      return {
        completed: false,
        results: {
          totalDeudas: 0,
          porcentajeEnMora: 0,
          cumpleMinAcreedores: false,
          acreedoresMora: 0,
          acreedoresFaltantes: minimoAcreedores,
          montoTotalEnMora: 0,
          montoFaltante: 0,
          porcentajeFaltante: porcentajeMinimo,
          porcentajeMinimo: porcentajeMinimo,
        },
      };
    }
    // Convertir los montos string a números y calcular totales
    const totalDeudas = deudas.reduce((sum, deuda) => sum + Number(deuda.capital), 0);
    const deudasEnMora = deudas.filter((deuda) => deuda.rango_mora === "MAS DE 90 DIAS");
    const acreedoresMora = deudasEnMora.length;
    const montoTotalEnMora = deudasEnMora.reduce((sum, deuda) => sum + Number(deuda.capital), 0);
    // Cálculos de porcentajes y montos faltantes
    const porcentajeEnMora = (montoTotalEnMora / totalDeudas) * 100;
    const acreedoresFaltantes = Math.max(0, minimoAcreedores - acreedoresMora);
    const montoMinimo = totalDeudas * (porcentajeMinimo / 100);
    const montoFaltante = Math.max(0, montoMinimo - montoTotalEnMora);
    const porcentajeFaltante = Math.max(0, porcentajeMinimo - porcentajeEnMora);
    // Validaciones
    const cumpleMinAcreedores = acreedoresMora >= minimoAcreedores;
    const cumplePorcentajeMora = porcentajeEnMora >= porcentajeMinimo;
    const esValido = cumpleMinAcreedores && cumplePorcentajeMora;

    return {
      completed: esValido,
      error: esValido ? false : true,
      errorMessage: undefined,
      results: {
        deudas: deudas,
        totalDeudas: totalDeudas,
        porcentajeEnMora: porcentajeEnMora,
        cumpleMinAcreedores: cumpleMinAcreedores,
        acreedoresMora: acreedoresMora,
        acreedoresFaltantes: acreedoresFaltantes,
        montoTotalEnMora: montoTotalEnMora,
        montoFaltante: montoFaltante,
        porcentajeFaltante: porcentajeFaltante,
        porcentajeMinimo: porcentajeMinimo,
      },
    };
  } catch (error) {
    console.error("Error al validar deudas:", error);
    return {
      completed: false,
      error: true,
      errorMessage: "Error al validar las deudas.",
      results: {},
    };
  }
};

const VerificacionDeuda = ({
  procesoId,
  onStepValidation = null,
  showResults = false,
  initialData = null,
}) => {
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [analisis, setAnalisis] = useState(
    initialData || {
      completed: false,
      error: false,
      results: {
        deudas: [],
        totalDeudas: 0,
        porcentajeEnMora: 0,
        cumpleMinAcreedores: false,
        acreedoresMora: 0,
        acreedoresFaltantes: 0,
        montoTotalEnMora: 0,
        montoFaltante: 0,
        porcentajeFaltante: 0,
      },
    }
  );

  useEffect(() => {
    const cargarDeudas = async () => {
      if (initialData) {
        setAnalisis(initialData);
        if (onStepValidation) {
          onStepValidation(initialData.completed);
        }
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const resultado = await validateDeudas(procesoId);
        setAnalisis(resultado);
        if (onStepValidation) {
          onStepValidation(resultado.completed);
        }
      } catch (error) {
        console.error("Error al cargar las Deudas de la Solicitud:", error);
        setError(obtenerMensajeError(error, "Error al cargar las deudas de la Solicitud"));
        if (onStepValidation) {
          onStepValidation(false, "Error al cargar las Deudas de la Solicitud");
        }
      } finally {
        setLoading(false);
      }
    };

    cargarDeudas();
  }, [procesoId, onStepValidation, initialData]);

  if (loading) {
    return (
      <SuiBox sx={styles.loadingContainer}>
        <CircularProgress />
      </SuiBox>
    );
  }

  if (error) {
    return (
      <SuiBox sx={styles.errorContainer}>
        <SuiAlert color="error">{error}</SuiAlert>
      </SuiBox>
    );
  }

  if (!showResults) {
    return null;
  }

  if (!analisis.deudas.length) {
    return (
      <SuiBox sx={styles.errorContainer}>
        <SuiAlert color="warning">No hay acreencias registradas para validar</SuiAlert>
      </SuiBox>
    );
  }

  return (
    <Box sx={styles.root}>
      <SuiBox mb={3} sx={styles.resumenContainer}>
        <SuiTypography variant="button" fontWeight="regular">
          Total de Deudas: ${analisis.results.totalDeudas.toLocaleString()}
        </SuiTypography>
        <SuiTypography variant="button" fontWeight="regular" display="block">
          Porcentaje en Mora: {analisis.results.porcentajeEnMora.toFixed(2)}%
        </SuiTypography>
      </SuiBox>

      <SuiBox sx={styles.deudasContainer}>
        {analisis.deudas.map((deuda) => (
          <Box
            key={deuda.id}
            sx={[
              styles.deudaBase,
              deuda.rango_mora == "MAS DE 90 DIAS" ? styles.deudaMora : styles.deudaNoMora,
            ]}
          >
            <SuiTypography variant="button" fontWeight="medium">
              {deuda.str_acreedor?.nombre || "Sin nombre"}
            </SuiTypography>
            <SuiBox sx={styles.deudaInfo}>
              <SuiTypography variant="caption">
                ${parseInt(deuda.capital).toLocaleString()}
              </SuiTypography>
              <SuiTypography
                variant="caption"
                fontWeight="medium"
                color={deuda.rango_mora == "MAS DE 90 DIAS" ? "success" : "error"}
              >
                {deuda.porcentaje.toFixed(2)} %{" "}
                {deuda.rango_mora == "MAS DE 90 DIAS" ? "(En mora)" : "(No en mora)"}
              </SuiTypography>
            </SuiBox>
          </Box>
        ))}
      </SuiBox>
    </Box>
  );
};

VerificacionDeuda.propTypes = {
  procesoId: PropTypes.number.isRequired,
  onStepValidation: PropTypes.func,
  showResults: PropTypes.bool,
  initialData: PropTypes.object,
};

export default VerificacionDeuda;
